import { toast } from '@apps/web/lib/app_notifications';
import { type Mode } from '@libraries/typescript/api_client';

const AUTO_SWITCH_TOAST_ID = 'auto-switch-context';
const AUTO_SWITCH_TOAST_DELAY_MS = 200;
let pendingAutoSwitchToastTimeoutId: number | null = null;

type ShowAutoSwitchToastParams = {
    readonly organizationName?: string | null;
    readonly mode?: Mode | null;
    readonly resourceLabel?: string | null;
};

function formatMode(mode: Mode): string {
    return mode === 'sandbox' ? 'sandbox' : 'live';
}

function getTargetLabel(resourceLabel?: string | null): string {
    if (!resourceLabel) {
        return 'this page';
    }

    return `this ${resourceLabel}`;
}

function buildToastDescription({
    organizationName,
    mode,
    resourceLabel,
}: ShowAutoSwitchToastParams): string | null {
    const targetLabel = getTargetLabel(resourceLabel);

    if (organizationName && mode) {
        return `Switched to ${organizationName} in ${formatMode(mode)} mode to open ${targetLabel}.`;
    }

    if (organizationName) {
        return `Switched to ${organizationName} to open ${targetLabel}.`;
    }

    if (mode) {
        return `Switched to ${formatMode(mode)} mode to open ${targetLabel}.`;
    }

    return null;
}

function showToastDescription(description: string): void {
    toast.info('Context updated', {
        persistNotification: true,
        notificationType: 'app_feedback',
        id: AUTO_SWITCH_TOAST_ID,
        description,
        position: 'top-center',
    });
}

export function showAutoSwitchToast(params: ShowAutoSwitchToastParams): void {
    const description = buildToastDescription(params);

    if (!description) {
        return;
    }

    showToastDescription(description);
}

export function scheduleAutoSwitchToast(
    params: ShowAutoSwitchToastParams,
): void {
    const description = buildToastDescription(params);

    if (!description) {
        return;
    }

    if (pendingAutoSwitchToastTimeoutId !== null) {
        window.clearTimeout(pendingAutoSwitchToastTimeoutId);
    }

    pendingAutoSwitchToastTimeoutId = window.setTimeout(() => {
        pendingAutoSwitchToastTimeoutId = null;
        showToastDescription(description);
    }, AUTO_SWITCH_TOAST_DELAY_MS);
}
